/**
 * Channel Configuration API
 * არხების კონფიგურაციის API
 *
 * Manages channel CRUD operations and webhook configuration for all supported platforms
 */

import { get, post, patch, del } from '@aiia/portal-core';

export type ChannelType = 'TELEGRAM' | 'WHATSAPP' | 'VIBER' | 'MESSENGER' | 'INSTAGRAM';
export type ChannelStatus = 'PENDING' | 'ACTIVE' | 'DISABLED' | 'ERROR';

export interface ChannelConfig {
  id: string;
  widgetId: string;
  channelType: ChannelType;
  botUsername: string;
  status: ChannelStatus;
  errorMessage?: string;
  connectedAt?: string;
  lastActivityAt?: string;
  createdAt: string;
  webhookUrl?: string;
  verifyToken?: string;
}

// =============== Connect Channel Requests ===============

export interface ConnectTelegramRequest {
  widgetId: string;
  botToken: string;
}

export interface ConnectWhatsAppRequest {
  widgetId: string;
  phoneNumberId: string;
  accessToken: string;
}

export interface ConnectViberRequest {
  widgetId: string;
  authToken: string;
}

export interface ConnectMessengerRequest {
  widgetId: string;
  pageAccessToken: string;
}

export interface ConnectInstagramRequest {
  widgetId: string;
  pageAccessToken: string;
}

export interface UpdateChannelStatusRequest {
  enabled: boolean;
}

// =============== API Functions ===============

// Get all channels for a widget
export const getWidgetChannels = (widgetId: string) =>
  get<ChannelConfig[]>(`/channels/widget/${widgetId}`);

// Get channel by ID
export const getChannel = (channelId: string) =>
  get<ChannelConfig>(`/channels/${channelId}`);

// Connect Telegram bot
export const connectTelegram = (data: ConnectTelegramRequest) =>
  post<ChannelConfig>('/channels/telegram', data);

// Connect WhatsApp Business
export const connectWhatsApp = (data: ConnectWhatsAppRequest) =>
  post<ChannelConfig>('/channels/whatsapp', data);

// Connect Viber bot
export const connectViber = (data: ConnectViberRequest) =>
  post<ChannelConfig>('/channels/viber', data);

// Connect Facebook Messenger
export const connectMessenger = (data: ConnectMessengerRequest) =>
  post<ChannelConfig>('/channels/messenger', data);

// Connect Instagram Direct
export const connectInstagram = (data: ConnectInstagramRequest) =>
  post<ChannelConfig>('/channels/instagram', data);

// Enable/Disable channel
export const updateChannelStatus = (channelId: string, data: UpdateChannelStatusRequest) =>
  patch<ChannelConfig>(`/channels/${channelId}/status`, data);

// Disconnect (delete) channel
export const disconnectChannel = (channelId: string) =>
  del<void>(`/channels/${channelId}`);

// =============== Channel Info ===============

export interface ChannelInfo {
  type: ChannelType;
  name: string;
  description: string;
  icon: string;
  color: string;
  fields: ChannelField[];
}

export interface ChannelField {
  name: string;
  label: string;
  type: 'text' | 'password';
  placeholder: string;
  helpText?: string;
}

export const CHANNEL_INFO: Record<ChannelType, ChannelInfo> = {
  TELEGRAM: {
    type: 'TELEGRAM',
    name: 'Telegram',
    description: 'Connect your Telegram bot to receive messages',
    icon: '✈️',
    color: '#0088cc',
    fields: [
      {
        name: 'botToken',
        label: 'Bot Token',
        type: 'password',
        placeholder: '123456789:ABCdefGHIjklMNOpqrsTUVwxyz',
        helpText: 'Get this from @BotFather on Telegram',
      },
    ],
  },
  WHATSAPP: {
    type: 'WHATSAPP',
    name: 'WhatsApp Business',
    description: 'Connect WhatsApp Business API',
    icon: '💬',
    color: '#25D366',
    fields: [
      {
        name: 'phoneNumberId',
        label: 'Phone Number ID',
        type: 'text',
        placeholder: '123456789012345',
        helpText: 'From Meta Developer Portal',
      },
      {
        name: 'accessToken',
        label: 'Access Token',
        type: 'password',
        placeholder: 'EAAxxxxxxxxxx...',
        helpText: 'Permanent access token from Meta',
      },
    ],
  },
  VIBER: {
    type: 'VIBER',
    name: 'Viber',
    description: 'Connect your Viber bot',
    icon: '📱',
    color: '#7360F2',
    fields: [
      {
        name: 'authToken',
        label: 'Auth Token',
        type: 'password',
        placeholder: 'Your Viber auth token',
        helpText: 'From partners.viber.com',
      },
    ],
  },
  MESSENGER: {
    type: 'MESSENGER',
    name: 'Facebook Messenger',
    description: 'Connect your Facebook Page',
    icon: '💭',
    color: '#0084FF',
    fields: [
      {
        name: 'pageAccessToken',
        label: 'Page Access Token',
        type: 'password',
        placeholder: 'EAAxxxxxxxxxx...',
        helpText: 'From Meta Developer Portal with pages_messaging permission',
      },
    ],
  },
  INSTAGRAM: {
    type: 'INSTAGRAM',
    name: 'Instagram Direct',
    description: 'Connect your Instagram Business account',
    icon: '📸',
    color: '#E4405F',
    fields: [
      {
        name: 'pageAccessToken',
        label: 'Page Access Token',
        type: 'password',
        placeholder: 'EAAxxxxxxxxxx...',
        helpText: 'Same token as Messenger (linked Facebook Page)',
      },
    ],
  },
};
